import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdBanner from '../components/AdBanner';
import SEO from '../components/SEO';
import './ColorPicker.css';

function ColorPicker() {
  const [selectedColor, setSelectedColor] = useState('#3498db');
  const [colorFormat, setColorFormat] = useState('hex');
  const [colorHistory, setColorHistory] = useState([]);
  const [palette, setPalette] = useState([]);
  const [paletteType, setPaletteType] = useState('complementary');
  const canvasRef = useRef(null);


  // Convert hex to RGB
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  };

  // Convert RGB to hex
  const rgbToHex = (r, g, b) => {
    return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
  };

  // Convert RGB to HSL
  const rgbToHsl = (r, g, b) => {
    r /= 255;
    g /= 255;
    b /= 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;

    if (max === min) {
      h = s = 0; // achromatic
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
        default: h = 0;
      }
      h /= 6;
    }

    return {
      h: Math.round(h * 360),
      s: Math.round(s * 100),
      l: Math.round(l * 100)
    };
  };

  // Convert HSL to RGB
  const hslToRgb = (h, s, l) => {
    h /= 360;
    s /= 100;
    l /= 100;
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    const hue2rgb = (t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1/6) return p + (q - p) * 6 * t;
      if (t < 1/2) return q;
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
      return p;
    };
    const r = Math.round(hue2rgb(h + 1/3) * 255);
    const g = Math.round(hue2rgb(h) * 255);
    const b = Math.round(hue2rgb(h - 1/3) * 255);
    return { r, g, b };
  };

  // Generate color palette
  const generatePalette = (baseColor, type) => {
    const rgb = hexToRgb(baseColor);
    const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);
    let colors = [];

    switch (type) {
      case 'complementary':
        colors = [
          baseColor,
          rgbToHex(...Object.values(hslToRgb((hsl.h + 180) % 360, hsl.s, hsl.l)))
        ];
        break;
      case 'triadic':
        colors = [
          baseColor,
          rgbToHex(...Object.values(hslToRgb((hsl.h + 120) % 360, hsl.s, hsl.l))),
          rgbToHex(...Object.values(hslToRgb((hsl.h + 240) % 360, hsl.s, hsl.l)))
        ];
        break;
      case 'analogous':
        colors = [
          rgbToHex(...Object.values(hslToRgb((hsl.h - 30 + 360) % 360, hsl.s, hsl.l))),
          baseColor,
          rgbToHex(...Object.values(hslToRgb((hsl.h + 30) % 360, hsl.s, hsl.l)))
        ];
        break;
      case 'monochromatic':
        colors = [
          rgbToHex(...Object.values(hslToRgb(hsl.h, hsl.s, Math.max(10, hsl.l - 40)))),
          rgbToHex(...Object.values(hslToRgb(hsl.h, hsl.s, Math.max(10, hsl.l - 20)))),
          baseColor,
          rgbToHex(...Object.values(hslToRgb(hsl.h, hsl.s, Math.min(90, hsl.l + 20)))),
          rgbToHex(...Object.values(hslToRgb(hsl.h, hsl.s, Math.min(90, hsl.l + 40))))
        ];
        break;
      case 'tetradic':
        colors = [
          baseColor,
          rgbToHex(...Object.values(hslToRgb((hsl.h + 90) % 360, hsl.s, hsl.l))),
          rgbToHex(...Object.values(hslToRgb((hsl.h + 180) % 360, hsl.s, hsl.l))),
          rgbToHex(...Object.values(hslToRgb((hsl.h + 270) % 360, hsl.s, hsl.l)))
        ];
        break;
      default:
        colors = [baseColor];
    }

    setPalette(colors);
  };

  // Get color in specified format
  const getColorInFormat = (color, format) => {
    const rgb = hexToRgb(color);
    const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);

    switch (format) {
      case 'hex':
        return color.toUpperCase();
      case 'rgb':
        return `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`;
      case 'rgba':
        return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 1)`;
      case 'hsl':
        return `hsl(${hsl.h}, ${hsl.s}%, ${hsl.l}%)`;
      case 'hsla':
        return `hsla(${hsl.h}, ${hsl.s}%, ${hsl.l}%, 1)`;
      default:
        return color;
    }
  };

  // Add color to history
  const addToHistory = (color) => {
    if (!colorHistory.includes(color)) {
      const newHistory = [color, ...colorHistory.slice(0, 11)]; // Keep last 12 colors
      setColorHistory(newHistory);
    }
  };

  // Copy to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      // Could add a toast notification here
    });
  };

  // Generate random color
  const generateRandomColor = () => {
    const randomColor = '#' + Math.floor(Math.random()*16777215).toString(16).padStart(6, '0');
    setSelectedColor(randomColor);
    addToHistory(randomColor);
  };

  // Draw color picker canvas
  const drawCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;

    // Create gradient
    const gradient = ctx.createLinearGradient(0, 0, width, 0);
    gradient.addColorStop(0, '#ff0000');
    gradient.addColorStop(1/6, '#ffff00');
    gradient.addColorStop(2/6, '#00ff00');
    gradient.addColorStop(3/6, '#00ffff');
    gradient.addColorStop(4/6, '#0000ff');
    gradient.addColorStop(5/6, '#ff00ff');
    gradient.addColorStop(1, '#ff0000');

    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);

    // Add black-to-white gradient
    const gradient2 = ctx.createLinearGradient(0, 0, 0, height);
    gradient2.addColorStop(0, 'rgba(255, 255, 255, 1)');
    gradient2.addColorStop(0.5, 'rgba(255, 255, 255, 0)');
    gradient2.addColorStop(0.5, 'rgba(0, 0, 0, 0)');
    gradient2.addColorStop(1, 'rgba(0, 0, 0, 1)');

    ctx.fillStyle = gradient2;
    ctx.fillRect(0, 0, width, height);
  };

  // Handle canvas click
  const handleCanvasClick = (event) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    const ctx = canvas.getContext('2d');
    const imageData = ctx.getImageData(x, y, 1, 1);
    const [r, g, b] = imageData.data;

    const pickedColor = rgbToHex(r, g, b);
    setSelectedColor(pickedColor);
    addToHistory(pickedColor);
  };

  useEffect(() => {
    drawCanvas();
  }, []);

  useEffect(() => {
    generatePalette(selectedColor, paletteType);
  }, [selectedColor, paletteType]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    addToHistory(selectedColor);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const presetColors = [
    '#e74c3c', '#e67e22', '#f39c12', '#f1c40f', '#2ecc71', '#1abc9c',
    '#3498db', '#9b59b6', '#34495e', '#95a5a6', '#ecf0f1', '#bdc3c7'
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Color Picker - Color Palette Generator Tool",
    "description": "Free color picker and palette generator with HEX, RGB, HSL color formats. Create complementary, triadic, analogous, and monochromatic color schemes for design.",
    "url": "https://infi-tools.com/color-picker",
    "applicationCategory": "DesignApplication",
    "operatingSystem": "Web Browser",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "featureList": [
      "Color picker with canvas",
      "Multiple color formats (HEX, RGB, HSL, RGBA, HSLA)",
      "Color palette generator",
      "Complementary, triadic, analogous color schemes",
      "Color history tracking",
      "Preset colors",
      "Random color generator",
      "Copy to clipboard"
    ]
  };

  return (
    <div className="color-picker-page">
      <SEO 
        title="Color Picker - Free Color Palette Generator & Color Tool"
        description="Free color picker and palette generator with HEX, RGB, HSL formats. Create complementary, triadic, analogous color schemes. Perfect for designers and developers."
        keywords="color picker, color palette generator, hex color picker, rgb color picker, color scheme generator, complementary colors, triadic colors, color tool, design colors"
        canonicalUrl="https://infi-tools.com/color-picker"
        structuredData={structuredData}
      />
      <div className="container">
        <div className="page-header">
          <Link to="/" className="back-link">← Back to Tools</Link>
          <h1>Color Picker</h1>
          <p className="page-description">
            Pick colors, generate palettes, and convert between different color formats.
          </p>
        </div>

        {/* Top Ad Banner */}
        <AdBanner adSlot="3847691105" style={{ marginBottom: '20px' }} />

        <div className="color-picker-container">
          <div className="picker-section">
            <div className="main-color-display">
              <div 
                className="color-preview"
                style={{ backgroundColor: selectedColor }}
              />
              <div className="color-info">
                <h3>Selected Color</h3>
                <div className="color-value">
                  <input
                    type="text"
                    value={getColorInFormat(selectedColor, colorFormat)}
                    readOnly
                    onClick={(e) => {
                      e.target.select();
                      copyToClipboard(e.target.value);
                    }}
                  />
                  <button 
                    className="copy-btn"
                    onClick={() => copyToClipboard(getColorInFormat(selectedColor, colorFormat))}
                  >
                    📋
                  </button>
                </div>
              </div>
            </div>

            <div className="color-input-section">
              <div className="input-group">
                <label>Color Input:</label>
                <input
                  type="color"
                  value={selectedColor}
                  onChange={(e) => {
                    setSelectedColor(e.target.value);
                    addToHistory(e.target.value);
                  }}
                />
              </div>

              <div className="input-group">
                <label>HEX Value:</label>
                <input
                  type="text"
                  value={selectedColor}
                  onChange={(e) => {
                    const hex = e.target.value;
                    if (/^#[0-9A-F]{6}$/i.test(hex)) {
                      setSelectedColor(hex);
                      addToHistory(hex);
                    }
                  }}
                />
              </div>

              <div className="input-group">
                <label>Format:</label>
                <select
                  value={colorFormat}
                  onChange={(e) => setColorFormat(e.target.value)}
                >
                  <option value="hex">HEX</option>
                  <option value="rgb">RGB</option>
                  <option value="rgba">RGBA</option>
                  <option value="hsl">HSL</option>
                  <option value="hsla">HSLA</option>
                </select>
              </div>

              <button className="btn random-btn" onClick={generateRandomColor}>
                🎲 Random Color
              </button>
            </div>

            <div className="canvas-section">
              <h3>Color Picker Canvas</h3>
              <canvas
                ref={canvasRef}
                width="300"
                height="200"
                onClick={handleCanvasClick}
                className="color-canvas"
              />
              <p className="canvas-info">Click on the canvas to pick a color</p>
            </div>

            <div className="preset-colors">
              <h3>Preset Colors</h3>
              <div className="preset-grid">
                {presetColors.map((color, index) => (
                  <div
                    key={index}
                    className="preset-color"
                    style={{ backgroundColor: color }}
                    onClick={() => {
                      setSelectedColor(color);
                      addToHistory(color);
                    }}
                    title={color}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="palette-section">
            <div className="palette-generator">
              <h3>Color Palette Generator</h3>
              <div className="palette-controls">
                <label>Palette Type:</label>
                <select
                  value={paletteType}
                  onChange={(e) => setPaletteType(e.target.value)}
                >
                  <option value="complementary">Complementary</option>
                  <option value="triadic">Triadic</option>
                  <option value="analogous">Analogous</option>
                  <option value="monochromatic">Monochromatic</option>
                  <option value="tetradic">Tetradic</option>
                </select>
              </div>

              <div className="palette-display">
                {palette.map((color, index) => (
                  <div key={index} className="palette-color">
                    <div
                      className="palette-swatch"
                      style={{ backgroundColor: color }}
                      onClick={() => {
                        setSelectedColor(color);
                        addToHistory(color);
                      }}
                    />
                    <div className="palette-value">
                      <span 
                        onClick={() => copyToClipboard(color)}
                        title="Click to copy"
                      >
                        {color}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="color-formats">
              <h3>All Formats</h3>
              <div className="format-list">
                {['hex', 'rgb', 'rgba', 'hsl', 'hsla'].map(format => (
                  <div key={format} className="format-item">
                    <label>{format.toUpperCase()}:</label>
                    <input
                      type="text"
                      value={getColorInFormat(selectedColor, format)}
                      readOnly
                      onClick={(e) => {
                        e.target.select();
                        copyToClipboard(e.target.value);
                      }}
                    />
                    <button 
                      className="copy-btn-small"
                      onClick={() => copyToClipboard(getColorInFormat(selectedColor, format))}
                    >
                      📋
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {colorHistory.length > 0 && (
              <div className="color-history">
                <h3>Recent Colors</h3>
                <div className="history-grid">
                  {colorHistory.map((color, index) => (
                    <div
                      key={index}
                      className="history-color"
                      style={{ backgroundColor: color }}
                      onClick={() => setSelectedColor(color)}
                      title={color}
                    />
                  ))}
                </div>
                <button 
                  className="clear-history-btn"
                  onClick={() => setColorHistory([])}
                >
                  Clear History
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Bottom Ad Banner */}
        <AdBanner adSlot="8073844756" style={{ marginTop: '40px' }} />
      </div>
    </div>
  );
}

export default ColorPicker;